import React from "react";
import { Smartphone, BellRing, CheckCircle2, Download, Share, PlusSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useInstallAction } from "@/components/InstallPrompt";
import { PushToggle } from "@/components/PushToggle";
import { usePush } from "@/hooks/usePush";

const Block = ({ icon: Icon, title, children, testId }) => (
  <section className="rounded-xl border border-border bg-card p-5" data-testid={testId}>
    <div className="flex items-center gap-2 font-heading text-base font-semibold"><Icon className="h-4 w-4 text-primary" /> {title}</div>
    <div className="mt-3 space-y-3 text-sm">{children}</div>
  </section>
);

export const PhoneCard = () => {
  const inst = useInstallAction();
  const push = usePush();
  return (
    <div className="grid gap-4 lg:grid-cols-2" data-testid="phone-card">
      {inst.iosDialog}
      <Block icon={Smartphone} title="D20 on your phone" testId="phone-install">
        <p className="text-muted-foreground">Install D20 on your home screen and it opens full-screen on the Approve screen — one invoice at a time, big Approve / Reject buttons, swipe to move on.</p>
        {inst.standalone ? (
          <p className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-500/10 px-3 py-2 text-emerald-600 dark:text-emerald-400" data-testid="phone-installed"><CheckCircle2 className="h-4 w-4" /> You're using the installed app.</p>
        ) : inst.native ? (
          <Button onClick={inst.run} data-testid="phone-install-btn"><Download className="mr-2 h-4 w-4" /> Install D20 on this device</Button>
        ) : (
          <div className="rounded-lg border border-border p-3">
            <div className="font-medium">{inst.ios ? "On this iPhone / iPad" : "On your phone"}</div>
            <ol className="mt-2 space-y-1.5 text-muted-foreground">
              <li className="flex items-start gap-2"><span className="flex h-6 w-6 shrink-0 items-center justify-center rounded bg-primary/10 text-primary text-xs font-semibold">1</span><span>Open <b className="text-foreground break-all">{window.location.host}</b> in {inst.ios ? "Safari" : "Chrome"} on the phone and sign in.</span></li>
              <li className="flex items-start gap-2"><span className="flex h-6 w-6 shrink-0 items-center justify-center rounded bg-primary/10 text-primary text-xs font-semibold">2</span><span>{inst.ios ? <>Tap <Share className="inline h-3.5 w-3.5" /> <b className="text-foreground">Share</b> → <PlusSquare className="inline h-3.5 w-3.5" /> <b className="text-foreground">Add to Home Screen</b>.</> : <>Tap the browser menu → <b className="text-foreground">Install app</b> (or accept the install banner on the Approve screen).</>}</span></li>
            </ol>
          </div>
        )}
      </Block>
      <Block icon={BellRing} title="Notifications on this device" testId="phone-notifications">
        <p className="text-muted-foreground">Get a notification the moment an invoice is handed to you or a teammate @mentions you in a note. Tapping it opens that invoice in the Approve screen. Nothing else is sent.</p>
        {!push.supported || !push.configured ? (
          <p className="text-muted-foreground" data-testid="phone-push-unavailable">{push.configured ? "This browser doesn't support notifications." : "Notifications aren't configured on this server."}</p>
        ) : (
          <div className="flex flex-wrap items-center gap-3">
            <PushToggle />
            <span className="text-xs text-muted-foreground" data-testid="phone-push-devices">{push.devices === 0 ? "No devices yet" : `${push.devices} device${push.devices === 1 ? "" : "s"} subscribed`}</span>
          </div>
        )}
        {push.needsInstall && <p className="text-xs text-amber-600 dark:text-amber-400">On iPhone, notifications only work from the installed home-screen app — install first, then turn them on there.</p>}
      </Block>
    </div>
  );
};
